import {styled, Typography} from '@mui/material';
import {useRef} from 'react';

import {CustomDialogItem} from 'filters/features/FilterDialog/types';
import {PolicyStatusType, AggPolicyStatus, PolicyStatusFilter} from 'modules/decisionsNext/api/types';

import {useIsDownMd} from 'utils/hooks/useMediaQuery';
import {useFeatureFlag} from 'core/hooks/authHooks';
import {FeatureFlags} from 'core/constants/featureFlags';
import {usePolicyStatusesTree} from 'modules/decisionsNext/hooks/trees/usePolicyStatusesTree';
import {useEditedFilterContext} from 'filters/features/FilterDialog/contexts/internal/EditedFilterProvider/EditedFilterProvider';
import {ungroupSelectedKeys, groupSelectedKeys, groupSelectedKeysByLevel} from 'utils/treeUtils';

import {FilterTreePicker} from 'components/TreePicker/FilterTreePicker';
import {useOptimizedSelection} from 'components/Tree/hooks/useOptimizedSelection';

import {Block} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/Block';
import {
  getPolicyStatusTypeHelperText,
  policyStatusOptions,
} from 'modules/decisionsNext/components/BaseFilter/utils/period';
import {RadioGroupBlock} from 'modules/decisionsNext/components/BaseFilter/Dialog/components/RadioGroupBlock';

export type PolicyStatusFieldValue = {
  type?: PolicyStatusType;
  statusFilter?: PolicyStatusFilter;
};

type PolicyStatusConfig = CustomDialogItem<PolicyStatusFieldValue>;

export const createPolicyStatusItem = (options: Omit<PolicyStatusConfig, 'Component'>): PolicyStatusConfig => {
  return {...options, Component: PolicyStatusItem};
};

const SRoot = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const SFilterTreePicker = styled(FilterTreePicker)(({theme}) => ({
  padding: 0,
  height: 316,
  minHeight: 316,

  '& > div': {
    gap: theme.spacing(1),
  },
})) as unknown as typeof FilterTreePicker;

export const PolicyStatusItem: PolicyStatusConfig['Component'] = ({getValue, onChange}) => {
  const isDownMd = useIsDownMd();
  const {filter, setFilter} = useEditedFilterContext();
  const {type, statusFilter} = getValue(filter);

  const withPolicyStatusFilter = useFeatureFlag(FeatureFlags.PolicyStatusFilter);
  const statusesRef = useRef<HTMLDivElement>(null);
  const prevStatusesRef = useRef<Record<PolicyStatusType, PolicyStatusFilter | null>>(
    {} as Record<PolicyStatusType, PolicyStatusFilter | null>
  );

  const {treeData, loading} = usePolicyStatusesTree(type);

  const handleTypeChange = (newType: PolicyStatusType) => {
    const prevPolicyStatuses = prevStatusesRef.current?.[newType] || (type === newType ? statusFilter : null);

    setFilter(prev =>
      onChange(prev, {
        type: newType,
        statusFilter: prevPolicyStatuses ?? undefined,
      })
    );

    if (type) {
      prevStatusesRef.current = {
        ...prevStatusesRef.current,
        [type]: statusFilter,
      };
    }

    setTimeout(() => {
      statusesRef?.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }, 50);
  };

  const selected = Array.from(
    ungroupSelectedKeys(treeData, [...(statusFilter?.statuses || []), ...(statusFilter?.codes || [])])
  );

  const setSelected = (newSelected: string[]) => {
    const groupedKeys = groupSelectedKeys(treeData, newSelected);
    const selectedByLevel = groupSelectedKeysByLevel(treeData, groupedKeys);

    setFilter(prev =>
      onChange(prev, {
        type,
        statusFilter: newSelected.length
          ? {
              statuses: selectedByLevel[0] as AggPolicyStatus[],
              codes: selectedByLevel[1],
            }
          : undefined,
      })
    );
  };

  const setSelectedNone = () => setFilter(prev => onChange(prev, {type, statusFilter: undefined}));

  const policyStatusesList = (
    <SFilterTreePicker
      ref={statusesRef}
      treeData={treeData}
      treeHeight={212}
      withSearch
      isLoading={loading}
      entity='Policy Status'
      {...useOptimizedSelection({
        treeData,
        selected,
        setSelected,
        setSelectedNone,
      })}
      selectionCount={
        selected.length ? (statusFilter?.statuses?.length || 0) + (statusFilter?.codes?.length || 0) : 'All'
      }
    />
  );

  if (!withPolicyStatusFilter) {
    return (
      <SRoot>
        <Block
          label='Policy Status'
          helperText={
            <>
              <Typography variant='body3Bold' color='text.secondary'>
                ennabl Policy Status
              </Typography>
              : {getPolicyStatusTypeHelperText()}
            </>
          }
        />

        {policyStatusesList}
      </SRoot>
    );
  }

  return (
    <SRoot>
      <RadioGroupBlock
        data-testid='base-period-policy-status'
        row={!isDownMd}
        label='Policy Status'
        options={policyStatusOptions}
        value={type}
        onChange={(_, value) => handleTypeChange(value as PolicyStatusType)}
        helperText={getPolicyStatusTypeHelperText()}
      />

      {policyStatusesList}
    </SRoot>
  );
};
